/**
 * @file: ReservationItemsPanel.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The booked-items panel, modeled on the lead-detail covered-items section: a
 * header add-action, the same per-type glyphs, and a tap-row → action-sheet
 * remove (no inline button). Each item is a ServiceSubject captured at booking
 * (no assignee yet); the type drives the icon + label.
 */

import { Typography } from '@euler/app/components/typography';
import { LeadItemTypeIcon } from '@euler/app/flows/leads/pages/detail/components/LeadItemTypeIcon';
import { Card } from '@euler/app/flows/leads/pages/detail/components/common/Card';
import { SectionAction } from '@euler/app/flows/leads/pages/detail/components/common/SectionAction';
import { SectionHeader } from '@euler/app/flows/leads/pages/detail/components/common/SectionHeader';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ServiceSubjectType } from '@euler/model/enum';
import { ReservationItem } from '@euler/model/reservation/ReservationItem';
import { memo } from 'react';
import { TouchableOpacity, View } from 'react-native';

// Map the service-subject type to the lead glyph key (unknown → "other"
// fallback) and a human label shown beneath the name.
const kIconType: Record<ServiceSubjectType, string> = {
  [ServiceSubjectType.Inspection]: 'inspection_subject',
  [ServiceSubjectType.Fault]: 'fault_like_subject',
  [ServiceSubjectType.Construction]: 'construction_job',
  [ServiceSubjectType.Other]: 'other',
};

const kTypeLabel: Record<ServiceSubjectType, string> = {
  [ServiceSubjectType.Inspection]: '车辆检测',
  [ServiceSubjectType.Fault]: '故障检修',
  [ServiceSubjectType.Construction]: '维保施工',
  [ServiceSubjectType.Other]: '其他项目',
};

export const ReservationItemsPanel = memo(
  ({
    items,
    onAdd,
    onItemPress,
  }: {
    items: ReservationItem[];
    onAdd?: () => void;
    onItemPress?: (index: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <>
        <SectionHeader
          title={`预约项目 (${items.length})`}
          right={
            onAdd ? (
              <SectionAction label="添加项目" onPress={onAdd} />
            ) : undefined
          }
        />
        <Card>
          {items.length === 0 ? (
            <View style={{ paddingVertical: rpx(14) }}>
              <Typography.Text size={rpx(14)} color={LD.mist}>
                暂无预约项目
              </Typography.Text>
            </View>
          ) : (
            items.map((item, index) => (
              <TouchableOpacity
                key={`${item.name}-${index}`}
                activeOpacity={onItemPress ? 0.6 : 1}
                onPress={onItemPress ? () => onItemPress(index) : undefined}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingVertical: rpx(12),
                  borderTopWidth: index === 0 ? 0 : 1,
                  borderTopColor: LD.hairline,
                }}
              >
                <View
                  style={{
                    width: rpx(38),
                    height: rpx(38),
                    borderRadius: rpx(19),
                    backgroundColor: LD.brandSoft,
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: rpx(12),
                  }}
                >
                  <LeadItemTypeIcon
                    type={kIconType[item.type]}
                    size={rpx(26)}
                    color={LD.brand}
                  />
                </View>
                <View style={{ flex: 1, paddingRight: rpx(10) }}>
                  <Typography.Text size={rpx(15)} color={LD.ink} bold>
                    {item.name}
                  </Typography.Text>
                  <Typography.Text size={rpx(12)} color={LD.mist} mt={rpx(3)}>
                    {kTypeLabel[item.type]}
                  </Typography.Text>
                </View>
              </TouchableOpacity>
            ))
          )}
        </Card>
      </>
    );
  },
);
